import { getErrorResponse } from "./errorUtils"
import type { CheckConflictsErrorCode, MeetingMutationErrorCode } from "./types"

const MEETING_MUTATION_ERROR_CODES = [
  "start_at_must_be_in_future",
  "scheduling_blocked",
  "too_many_meetings_per_day",
  "too_many_emails_per_day",
  "editing_limit_reached",
  "plan_limit",
] as const satisfies readonly MeetingMutationErrorCode[]

const MEETING_MUTATION_ERROR_STATUSES = new Set([400, 403])

type MeetingMutationOperation = "create" | "edit"

class MeetingMutationError extends Error {
  readonly code: MeetingMutationErrorCode
  readonly operation: MeetingMutationOperation

  constructor(
    operation: MeetingMutationOperation,
    code: MeetingMutationErrorCode,
    options?: ErrorOptions,
  ) {
    super(`Failed to ${operation} meeting: ${code}`, options)
    this.name = "MeetingMutationError"
    this.code = code
    this.operation = operation
  }
}

const isMeetingMutationErrorCode = (
  value: unknown,
): value is MeetingMutationErrorCode => {
  return (
    typeof value === "string" &&
    MEETING_MUTATION_ERROR_CODES.includes(value as MeetingMutationErrorCode)
  )
}

const normalizeMeetingMutationError = (
  error: unknown,
  operation: MeetingMutationOperation,
): unknown => {
  const response = getErrorResponse(error)

  if (
    response?.status != null &&
    MEETING_MUTATION_ERROR_STATUSES.has(response.status) &&
    isMeetingMutationErrorCode(response.data)
  ) {
    return new MeetingMutationError(operation, response.data, {
      cause: error,
    })
  }

  return error
}

export const normalizeCreateMeetingError = (error: unknown): unknown => {
  return normalizeMeetingMutationError(error, "create")
}

export const normalizeEditMeetingError = (error: unknown): unknown => {
  return normalizeMeetingMutationError(error, "edit")
}

export const isMeetingMutationError = (
  error: unknown,
): error is MeetingMutationError => {
  return error instanceof MeetingMutationError
}

class CheckConflictsError extends Error {
  readonly code: CheckConflictsErrorCode

  constructor(code: CheckConflictsErrorCode, options?: ErrorOptions) {
    super(`Failed to check meeting conflicts: ${code}`, options)
    this.name = "CheckConflictsError"
    this.code = code
  }
}

export const normalizeCheckConflictsError = (error: unknown): unknown => {
  const response = getErrorResponse(error)

  if (response?.status === 429 && response.data === "rate_limited") {
    return new CheckConflictsError("rate_limited", { cause: error })
  }

  return error
}

export const isCheckConflictsError = (
  error: unknown,
): error is CheckConflictsError => {
  return error instanceof CheckConflictsError
}
